import { useTranslation } from 'react-i18next'
import { MessageSquare, Pin } from 'lucide-react'

import { ThreadRowMenu } from '@/components/shared/ThreadRowMenu'
import { KnowledgeThreadListItem } from '@/lib/api'

import { formatThreadRelativeTime, threadScopeChipLabel, threadSourceCountLabel } from './threadHelpers'

interface ThreadCardProps {
  thread: KnowledgeThreadListItem;
  onClick: () => void;
  onRename: (token: string, title: string) => Promise<void>;
  onPin: (token: string, pinned: boolean) => Promise<void>;
  onDelete: (token: string) => void;
}

export function ThreadCard(props: ThreadCardProps) {
  const { thread, onClick, onRename, onPin, onDelete } = props
  const { t } = useTranslation('app')

  const title = thread.title || t('threads.untitled')
  const scopeLabel = threadScopeChipLabel(thread.scope)
  const sourceLabel = threadSourceCountLabel(thread.citationCount)
  const relativeTime = formatThreadRelativeTime(thread.lastMessageAt || thread.updatedAt)

  return (
    <div className="group relative border-b border-border">
      <button
        type="button"
        onClick={onClick}
        data-testid={`threads-row-${thread.token}`}
        className="flex w-full items-center gap-3.5 px-4 py-3.5 pr-11 text-left transition-colors hover:bg-muted/40"
      >
        <div className="h-9 w-9 shrink-0 rounded-lg bg-muted flex items-center justify-center">
          <MessageSquare className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15px] font-semibold leading-snug text-foreground">
              {title}
            </span>
            {thread.pinned && (
              <Pin
                aria-hidden
                data-testid={`threads-row-pinned-${thread.token}`}
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                strokeWidth={2}
              />
            )}
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {scopeLabel}
            </span>
          </div>

          {thread.lastMessagePreview && (
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
              {thread.lastMessagePreview}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-0.5">
          {sourceLabel && (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {sourceLabel}
            </span>
          )}
          {relativeTime && (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {relativeTime}
            </span>
          )}
        </div>
      </button>

      {/* Rename, pin and delete are owner-only on the server. A shared thread a
          colleague can read shows no row menu at all rather than actions
          that answer 404. */}
      {thread.isOwner !== false && (
        <div
          className="absolute right-2 top-1/2 -translate-y-1/2"
          onClick={(e) => e.stopPropagation()}
        >
          <ThreadRowMenu
            token={thread.token}
            testIdPrefix="threads-row"
            title={thread.title}
            isPinned={thread.pinned === true}
            onRename={(nextTitle) => void onRename(thread.token, nextTitle)}
            onPin={() => void onPin(thread.token, thread.pinned !== true)}
            onDelete={() => onDelete(thread.token)}
          />
        </div>
      )}
    </div>
  )
}
